<template>
  <div :class="{'tags-input__hide-dropdown': disableSelection}" class="tags-input" >
    <div v-if="!hideIcon" class="tags-input__icon">
      <i class="ddi-tag ddi" />
    </div>
    <div :class="{'tags-input__content--bordered': showInputBorder}" class="tags-input__content">
      <el-select
        ref="tagInputSelect"
        v-model="internalSelectedTags"
        :allow-create="!searchOnly"
        :filter-method="handleCurInputValue"
        :teleported="false"
        :popper-options="popperOptions"
        :popper-class="dropdownClass"
        :disabled="isDisabled"
        filterable
        placeholder=""
        reserve-keyword
        multiple
        default-first-option
        @keyup.tab="handleDelimiter"
        @keyup.enter="handleDelimiter"
        @visible-change="handleVisibleChange"
      >
        <el-option
          v-for="tag in displayedTags"
          :key="tag"
          :label="tag"
          :value="tag"
        />
        <el-option
          v-if="hiddenTagsCount > 0"
          :value="0"
          disabled
          class="tags-input__dropdown-option-empty"
        >Type to find {{ hiddenTagsCount }} more…</el-option>
        <el-option
          v-if="filteredAvailableTags.length == 0 && searchOnly"
          :value="0"
          disabled
          class="tags-input__dropdown-option-empty"
        >No tags found</el-option>
      </el-select>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { ClickOutside as Clickoutside } from 'element-plus'
import PopperAssistant from '@/helpers/popper-assistant'

const MAX_RENDERED_TAGS = 100

/* The parts of el-select's instance that this component works with */
interface ISelectInternals {
  states: { inputValue: string }
  inputRef: HTMLInputElement
  selectionRef: HTMLElement
}

export default defineComponent({
  name: 'TagsInput',

  directives: { Clickoutside },

  props: {
    selectedTags: {
      type: Array as () => Array<string>,
      default: (): Array<string> => [],
    },
    availableTags: {
      type: Array as () => Array<string>,
      default: (): Array<string> => [],
    },
    searchOnly: {
      type: Boolean,
      default: false,
    },
    hideIcon: {
      type: Boolean,
      default: false,
    },
    showInputBorder: {
      type: Boolean,
      default: false,
    },
    disableSelection: {
      type: Boolean,
      default: false,
    },
    enableDropdownAbsolutePositioning: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:selectedTags'],

  data() {
    return {
      // Fixed inside dialogs and drawers, so their scrolling body doesn't cut the dropdown off
      popperOptions: {},
      filteredAvailableTags: [] as any[],
      curInputValue: '',
    }
  },

  computed: {
    internalSelectedTags: {
      get(): Array<string> {
        return this.selectedTags
      },
      set(newValue: Array<string>) {
        this.clearInputField()
        this.$emit('update:selectedTags', newValue)
      },
    },
    /*
    Element Plus mounts every option up front, even while the dropdown is closed, and an account can have thousands of
    tags (4,000 took 5 seconds), so only the first matches are rendered; typing narrows them down
    */
    displayedTags(): Array<string> {
      return this.filteredAvailableTags.slice(0, MAX_RENDERED_TAGS)
    },
    hiddenTagsCount(): number {
      return this.filteredAvailableTags.length - this.displayedTags.length
    },
    dropdownClass(): string {
      let result = 'tags-input__dropdown'
      if (this.enableDropdownAbsolutePositioning) {
        result += ' absolute'
      }
      return result
    },
  },

  watch: {
    availableTags: {
      deep: 1,
      immediate: true,
      handler(newValue) {
        this.filteredAvailableTags = newValue
      },
    },
  },

  mounted() {
    this.popperOptions = PopperAssistant.inPlacePopperOptions(this.$el)
    this.addInputPlaceholder()

    this.select().inputRef.addEventListener(
      'keyup',
      (event: KeyboardEvent) => {
        this.adjustDropdownPosition()
      },
    )
    /**
     * Prevent dropdown when clicking on tags
     */
    this.select().selectionRef.addEventListener(
      'click',
      (event: MouseEvent) => {
        event.stopPropagation()
      },
    )
  },

  methods: {
    select(): ISelectInternals {
      return this.$refs.tagInputSelect as unknown as ISelectInternals
    },

    addInputPlaceholder() {
      const input: HTMLInputElement = this.$el.querySelector(
        '.el-select__input',
      )

      input.placeholder = this.isDisabled
        ? ''
        : this.searchOnly
          ? 'Select tags...'
          : 'Add tags...'
    },

    pruneAvailableTags(inputValue: string) {
      this.filteredAvailableTags = this.availableTags.filter(i =>
        i.toLowerCase().includes(inputValue.toLowerCase()),
      )
    },

    attemptTagSubmit(inputValue: string) {
      const inputValueCleansed = inputValue.toLowerCase().trim()

      if (
        !this.internalSelectedTags.includes(inputValueCleansed) &&
        inputValueCleansed !== ''
      ) {
        const updatedTags = this.internalSelectedTags
        updatedTags.push(inputValueCleansed)
        this.internalSelectedTags = updatedTags
      }
    },

    /**
     * Handle filter and comma to add new tag
     */
    handleCurInputValue(inputValue: string) {
      /* Store the current value separately. This will be used
      in case user focuses out of the select field so we can evaluate
      whether the last typed-in value needs to be processed. Otherwise,
      the select's query is empty whenever we
      lose focus*/
      this.curInputValue = inputValue

      this.adjustDropdownPosition()
      this.pruneAvailableTags(inputValue)
      if (this.searchOnly) {
        return
      }

      const inputNoComma = inputValue.slice(0, -1)
      // Treat comma as a delimiter
      if (inputValue.endsWith(',')) {
        // Checking if includes comma because if you just hold "," sometimes
        // handleCurInputValue will pass a value of some number of commas because of debouncing
        if (inputNoComma !== '' && !inputNoComma.includes(',')) {
          this.attemptTagSubmit(inputNoComma)
        } else {
          this.clearInputField()
        }
      }
    },

    clearInputField() {
      this.curInputValue = ''
      this.select().states.inputValue = ''
    },

    /**
     * Handle enter on a certain tag
     */
    handleDelimiter() {
      if (this.searchOnly) {
        this.clearInputField()
        return
      }

      const inputValue = this.select().states.inputValue.trim()
      this.attemptTagSubmit(inputValue)
    },

    handleVisibleChange(value: boolean) {
      /* If we are clicking out of a non-search only picker, 
         process any remaining value in the input. */
      if (!value && !this.searchOnly) {
        this.attemptTagSubmit(this.curInputValue)
      }
    },

    /**
     * Change position of dropdown to align with the beginning of the input
     */
    adjustDropdownPosition() {
      const input: HTMLInputElement = this.$el.querySelector(
        '.el-select__input',
      )
      const dropdown: HTMLElement = this.$el.querySelector(
        '.el-select__popper',
      )
      if (dropdown) {
        dropdown.style.marginLeft = input.offsetLeft + 'px'
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.tags-input {
  display: flex;
  height: auto;
  color: var(--color-text-secondary);

  &__hide-dropdown {
    :deep(.el-select__popper) {
      visibility: hidden;
    }
  }

  &__icon {
    margin: 4px 4px 12px 8px;
  }

  &__content {
    display: flex;
    flex: 1 1 auto;
    border-top: 1px solid transparent;
    border-right: 1px solid transparent;
    border-bottom: 1px solid transparent;

    &--bordered {
      border: $border--solid-light;
      border-radius: 8px;
    }

    @include media-breakpoint-down(sm) {
      overflow: hidden;
      overflow-x: auto;
    }
  }

  &__dropdown-option-empty {
    display: flex;
    align-items: center;
    color: var(--color-text-third);
  }

  :deep(.el-select__input) {
    margin-left: 10px;
    min-width: 120px;
    @include lettering--p-sm;

    @include media-breakpoint-down(sm) {
      width: 100%;
    }
  }

  /* Element Plus hides an empty filter input until the select is focused, but ours holds the placeholder */
  :deep(.el-select__input-wrapper.is-hidden) {
    position: static;
    opacity: 1;
  }

  :deep(.el-select__selection) {
    max-width: 100% !important;

    /* Element Plus pulls the tags 8px into padding this select doesn't have; Element UI gave them a 6px margin */
    &.is-near {
      margin-left: 6px;
    }
  }

  /* The dropdown class is on the select's popper */
  :deep(.el-popper.tags-input__dropdown) {
    min-width: 300px !important;
    max-width: 328px !important;

    &.absolute {
      position: absolute !important;
      top: 40px !important;
    }

    @include media-breakpoint-up(md) {
      max-width: 300px !important;
    }
  }

  :deep(.el-select-dropdown__item) {
    display: flex;
    align-items: center;
  }

  :deep(.el-select) {
    flex: 1 1 auto;

    /* Only the tags and the filter input show: the select's own box is invisible */
    .el-select__wrapper,
    .el-select__wrapper.is-focused,
    .el-select__wrapper:hover {
      padding: 0;
      font-size: 15px;
      background-color: transparent;
      box-shadow: none;
      transition: none;
    }

    // Don't show caret
    .el-select__suffix {
      display: none;
    }

    .el-select__selection .el-tag {
      display: flex;
      align-items: center;
      padding: 1px 10px 0 10px;
      background: var(--color-background-secondary);
      border-radius: 20px;

      &:hover {
        filter: brightness(var(--hover-brightness));
      }
    }
  }

  .button {
    font-size: 16px;
    color: var(--color-text-third);
    text-decoration: none;
  }

  .ddi {
    top: 0;
    font-size: 0;
    line-height: 0;
    color: var(--color-text-third);

    &::before {
      width: 0;
      height: 0;
      font-size: 16px !important;
      line-height: 16px !important;
    }
  }
}

:deep(.tags-input__dropdown) {
  min-width: 100%;
  max-width: 100%;
  margin-left: 0;

  @include media-breakpoint-down(sm) {
    visibility: hidden;
  }

  .el-select-dropdown__empty {
    display: none;
  }
}
</style>
